"use client";

import { TaskFormBody, borderlessDescriptionClasses, borderlessInputClasses } from "../components/task-form-body";
import { AssigneeBadge, DateBadge, LocationBadge, PatientBadge, PickerPatient, PriorityBadge, StatusBadge, TeamBadge } from "../components/task-inline-editors";
import { useEffect, useState } from "react";
import { TaskMentionEditor } from "@/components/tiptap/task-mention-editor";
import Button from "@/components/ui/button";
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import Input from "@/components/ui/input";
import Switch from "@/components/ui/switch";
import { useUser } from "@/contexts/user-provider";
import { useLocations } from "@/hooks/use-locations";
import { CalendarDay, dayMarker, pickedDay } from "@/lib/clinic-clock";
import { parseError } from "@/lib/parse-error";
import { cx } from "@/lib/tailwind-utils";
import { toast } from "@/lib/use-toast";
import { TaskPriority, TaskStatus, TaskSummary } from "@/types/task";
import { useTaskMutations } from "@/api/provider/tasks/tasks-query";

// --- Main Component ---
interface CreateTaskModalProps {
  children?: React.ReactNode;
  onSuccess?: () => void;
  /** A confirmed patient the task is fixed to — hides the patient picker and
   *  submits this id. Use only when the patient is already established. */
  defaultPatientId?: string;
  /** Pre-fill the patient picker while keeping it visible and editable — for an
   *  unconfirmed suggestion the user must see and can change/clear before
   *  submitting. Ignored when defaultPatientId is set (that one is authoritative).
   *  Only the picker's display fields are needed, so callers holding a partial
   *  patient don't have to fabricate a full PatientSummary. */
  defaultSelectedPatient?: PickerPatient;
  /** Pre-fill the title when creating a new task (ignored in edit mode). */
  defaultTitle?: string;
  /** Pre-fill the description when creating a new task (ignored in edit mode). */
  defaultDescription?: string;
  /** Pre-select the assignee when creating a new task (ignored in edit mode). */
  defaultAssigneeId?: string;
  /** Require an assignee or team before the task can be created (new tasks only). */
  requireAssignee?: boolean;
  /** A note this task targets; auto-completed when that note is signed off (new tasks only). */
  targetNoteId?: string;
  /** A fax this task targets; the task deep-links to it ("Review fax" tasks, new tasks only). */
  targetFaxId?: string;
  /** A document this task targets; auto-completed when that document is signed off (new tasks only). */
  targetDocumentId?: string;
  /** Pre-select the priority when creating a new task (ignored in edit mode). */
  defaultPriority?: TaskPriority;
  /** Pre-select the team when creating a new task (ignored in edit mode). */
  defaultTeamId?: string;
  /** Pre-fill the due day when creating a new task (ignored in edit mode). */
  defaultDueDate?: CalendarDay;
  /**
   * The note an accepted AI scribe suggestion came from. Provenance and deep-link
   * only — unlike targetNoteId it does NOT enroll the task in the note sign-off
   * completion sweep, which would close the follow-up as soon as the note is signed.
   */
  sourceNoteId?: string;
  /** The scribe suggestion accepted here; the server stamps it so its card flips. */
  sourceTaskSuggestionId?: string;
  task?: TaskSummary;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}
export function CreateTaskModal({
  children,
  onSuccess,
  defaultPatientId,
  defaultSelectedPatient,
  defaultTitle,
  defaultDescription,
  defaultAssigneeId,
  requireAssignee = false,
  targetNoteId,
  targetFaxId,
  targetDocumentId,
  defaultPriority,
  defaultTeamId,
  defaultDueDate,
  sourceNoteId,
  sourceTaskSuggestionId,
  task,
  open: controlledOpen,
  onOpenChange
}: CreateTaskModalProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const isOpen = isControlled ? controlledOpen : internalOpen;
  const setIsOpen = isControlled ? onOpenChange! : setInternalOpen;
  const isEditMode = !!task;

  // A task pinned to a note, fax or document is meant to be the one review task
  // for that target — the fax record only remembers the first. The form keeps
  // the target id after a reset, so offering "Create another" here would quietly
  // produce duplicates that no longer link back from the thing they review.
  const isSingleTargetFlow = !!(targetNoteId || targetFaxId || targetDocumentId);

  // Form state
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState<TaskPriority>(TaskPriority.NORMAL);
  const [status, setStatus] = useState<TaskStatus>(TaskStatus.OPEN);
  const [dueDate, setDueDate] = useState<Date | undefined>();
  const [startDate, setStartDate] = useState<Date | undefined>();
  const [selectedPatient, setSelectedPatient] = useState<PickerPatient | undefined>();
  const [assigneeId, setAssigneeId] = useState<string | undefined>();
  const [teamId, setTeamId] = useState<string | undefined>();
  const [locationId, setLocationId] = useState<string | undefined>();
  // Staff write follow-ups in runs, so keep the dialog up and reset between
  // them. The patient survives the reset — a run is almost always about one
  // person — as do assignee/team, which are usually the same for a batch.
  const [createAnother, setCreateAnother] = useState(false);
  const {
    currentLocationId
  } = useUser();
  const {
    locations
  } = useLocations();
  const hasMultipleLocations = locations.length > 1;
  const {
    create,
    update
  } = useTaskMutations();
  const isSubmitting = create.isPending || update.isPending;
  useEffect(() => {
    if (task && isOpen) {
      setTitle(task.title);
      setDescription(task.description || "");
      setPriority(task.priority);
      setStatus(task.status);
      setDueDate(task.dueDate ? dayMarker(task.dueDate) : undefined);
      setStartDate(task.startDate ? dayMarker(task.startDate) : undefined);
      setSelectedPatient(task.patient ? {
        id: task.patient.id,
        firstName: task.patient.firstName,
        lastName: task.patient.lastName
      } : undefined);
      setAssigneeId(task.assigneeId || undefined);
      setTeamId(task.teamId || undefined);
      setLocationId(task.locationId || undefined);
    } else if (!task && isOpen) {
      // New task: start from a clean slate on every open so stale selections
      // from a previous (e.g. cancelled) task don't leak in, then apply any
      // caller-provided defaults (e.g. "Request sign-off" on a note).
      setTitle(defaultTitle ?? "");
      setDescription(defaultDescription ?? "");
      setPriority(defaultPriority ?? TaskPriority.NORMAL);
      setStatus(TaskStatus.OPEN);
      setDueDate(defaultDueDate ? dayMarker(defaultDueDate) : undefined);
      setStartDate(undefined);
      // A fixed patient (defaultPatientId) hides the picker and is applied at
      // submit; otherwise seed the visible picker with any suggested patient so
      // the user confirms it rather than it being auto-submitted unseen.
      setSelectedPatient(defaultPatientId ? undefined : defaultSelectedPatient);
      setAssigneeId(defaultAssigneeId);
      setTeamId(defaultTeamId);
      setLocationId(currentLocationId || undefined);
      // A run ends when the dialog closes; reopening starts a fresh one.
      setCreateAnother(false);
    }
    // currentLocationId is intentionally excluded: the default is seeded once
    // when the modal opens and must not overwrite a manual selection if the
    // current location resolves/changes afterward.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task, isOpen]);
  const resetForm = () => {
    setTitle("");
    setDescription("");
    setPriority(TaskPriority.NORMAL);
    setStatus(TaskStatus.OPEN);
    setSelectedPatient(undefined);
    setAssigneeId(undefined);
    setTeamId(undefined);
    setLocationId(undefined);
  };

  /** Clear only what differs between tasks in a run. */
  const resetForNextInRun = () => {
    setTitle("");
    setDescription("");
    setPriority(TaskPriority.NORMAL);
    setStatus(TaskStatus.OPEN);
    setDueDate(undefined);
    setStartDate(undefined);
  };
  const handleSubmit = async () => {
    if (!title.trim()) return;
    if (!isEditMode && requireAssignee && !assigneeId && !teamId) return;
    try {
      if (isEditMode && task) {
        await update.mutateAsync({
          taskId: task.id,
          task: {
            title: title.trim(),
            description: description.trim() || undefined,
            priority,
            status,
            dueDate: dueDate && pickedDay(dueDate),
            startDate: startDate && pickedDay(startDate),
            patientId: selectedPatient?.id,
            assigneeId: teamId ? null : assigneeId || null,
            teamId: teamId || null,
            locationId: locationId || null
          }
        });
      } else {
        await create.mutateAsync({
          title: title.trim(),
          description: description.trim() || undefined,
          priority,
          status,
          dueDate: dueDate && pickedDay(dueDate),
          startDate: startDate && pickedDay(startDate),
          patientId: selectedPatient?.id || defaultPatientId,
          assigneeId: teamId ? undefined : assigneeId,
          teamId,
          locationId,
          targetNoteId,
          targetFaxId,
          targetDocumentId,
          sourceNoteId,
          sourceTaskSuggestionId
        });
        onSuccess?.();
        if (createAnother) {
          // The dialog stays put and the form blanks, which on its own looks
          // like nothing happened. Say so explicitly.
          toast({
            variant: "success",
            title: "Task created"
          });
          resetForNextInRun();
          return;
        }
        resetForm();
        setIsOpen(false);
        return;
      }
      setIsOpen(false);
      onSuccess?.();
    } catch (error) {
      toast({
        variant: "error",
        title: isEditMode ? "Failed to update task" : "Failed to create task",
        description: await parseError(error, isEditMode ? "We couldn't update this task. Please try again." : "We couldn't create this task. Please try again.")
      });
    }
  };
  const handleOpenChange = (open: boolean) => {
    if (!isSubmitting) {
      setIsOpen(open);
      if (!open && !isEditMode) {
        resetForm();
      }
    }
  };
  return <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      {!!children && <DialogTrigger asChild>{children}</DialogTrigger>}
      <DialogContent className="max-w-2xl">
        {/* Title - borderless */}
        <DialogHeader className="md:pb-2 md:pt-4">
          <DialogTitle className="sr-only">
            {isEditMode ? "Edit Task" : "Create Task"}
          </DialogTitle>
          <Input value={title} onChange={e => setTitle(e.target.value)} placeholder={isEditMode ? "Edit task title" : "Task title"} className={borderlessInputClasses} autoFocus />
        </DialogHeader>

        {/* Description - borderless */}
        <DialogBody className="md:px-6 md:py-2">
          <TaskFormBody description={<TaskMentionEditor value={description} onChange={setDescription} placeholder="Add description..." className={cx(borderlessDescriptionClasses, "[&_p.is-empty]:!text-foreground-muted", "[&_p.is-editor-empty]:!text-foreground-muted")} />} badges={<>
                <StatusBadge value={status} onChange={setStatus} isEditMode={isEditMode} />
                <PriorityBadge value={priority} onChange={setPriority} />
                <AssigneeBadge value={assigneeId} onChange={v => {
            setAssigneeId(v);
            if (v) setTeamId(undefined);
          }} />
                <TeamBadge value={teamId} onChange={v => {
            setTeamId(v);
            if (v) setAssigneeId(undefined);
          }} />
                {hasMultipleLocations && <LocationBadge value={locationId} onChange={setLocationId} />}
                {!defaultPatientId && <PatientBadge value={selectedPatient} onChange={setSelectedPatient} />}
                <DateBadge label="Due date" value={dueDate} onChange={setDueDate} />
                <DateBadge label="Start date" value={startDate} onChange={setStartDate} />
              </>} />
        </DialogBody>

        {/* Metadata badges row */}
        <DialogFooter className="border-t md:p-2">
          {!isEditMode && !isSingleTargetFlow && <label className="mr-auto flex cursor-pointer select-none items-center gap-2 pl-2">
              <Switch checked={createAnother} onCheckedChange={setCreateAnother} disabled={isSubmitting} aria-label="Create another task after this one" />
              <span className="text-caption text-foreground-secondary">
                Create another
              </span>
            </label>}
          <Button variant="secondary" onClick={() => setIsOpen(false)} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSubmit} disabled={!title.trim() || !isEditMode && requireAssignee && !assigneeId && !teamId} isLoading={isSubmitting} loadingText={isEditMode ? "Saving..." : "Creating..."}>
            {isEditMode ? "Save Changes" : "Create Task"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>;
}